import React, { useCallback, useEffect, useState } from 'react';
import {
    Button, CircularProgress, FormControl, InputLabel, Select, MenuItem, TextField,
    DialogActions, DialogContent, DialogContentText, DialogTitle
} from '@material-ui/core';
import { Card } from 'react-bootstrap';
import EditIcon from '@material-ui/icons/Edit';
import SaveIcon from '@material-ui/icons/Save';
import CloseIcon from '@material-ui/icons/Close';
import CodeIcon from '@material-ui/icons/Code';
import ViewStreamIcon from '@material-ui/icons/ViewStream';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import WarningIcon from '@material-ui/icons/Warning';
import { useParams } from 'react-router-dom';
import request from 'tools/request';
import { Toast } from 'components/shared/Toaster';
import { Dialog } from 'components/shared/Dialog';
import { getDocusignTemplateConfig, configureDocusignTemplate } from 'actions/client';
import {
    getDocusignConfigByClientId,
    getDocusignAccountsByClientId,
    getDocusignTemplatesByClientId
} from 'actions/user';
import { DOCUSIGN_TEMPLATE_TYPES, DOCUSIGN_ROLE_NAMES, getTemplateTypeLabel } from 'constants/docusignTemplates';
import ConfigItem from '../ClientPortal/ConfigItem';
import DocusignRolesView from './DocusignRolesView';
import DocusignRolesEditor, { emptyRole } from './DocusignRolesEditor';
import { validateTemplateConfiguration, buildConfigurationPayload } from './docusignTemplateValidation';

// Plain textarea on purpose: codemirror is not a dependency of this project (it was added for the
// dynamic config editor and removed again in the "Rox 1834 demo" revert), and the JSON view of that
// editor settled on the same monospaced textarea.
const JsonEditor = ({ value, onChange, readOnly, error }) => (
    <div>
        <textarea
            value={value}
            readOnly={readOnly}
            onChange={(e) => onChange(e.target.value)}
            spellCheck={false}
            style={{
                width: '100%',
                minHeight: '420px',
                fontFamily: 'monospace',
                fontSize: '0.8rem',
                padding: '12px',
                borderRadius: '4px',
                border: error ? '1px solid #f44336' : '1px solid #ccc',
                resize: 'vertical',
                outline: 'none',
                backgroundColor: 'var(--bg-canvas)',
                lineHeight: 1.5,
                boxSizing: 'border-box'
            }}
        />
        {error && <p style={{ color: 'var(--status-declined)', fontSize: '0.8rem', marginTop: '6px' }}>{error}</p>}
    </div>
);

const ModeToggle = ({ mode, onChange, formLabel }) => (
    <div style={{ display: 'flex', border: '1px solid #ccc', borderRadius: '4px', overflow: 'hidden' }}>
        {[{ value: 'form', label: formLabel, icon: ViewStreamIcon }, { value: 'json', label: 'JSON', icon: CodeIcon }]
            .map((option, index) => {
                const Icon = option.icon;
                return (
                    <button
                        key={option.value}
                        type="button"
                        onClick={() => onChange(option.value)}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '4px',
                            padding: '5px 12px', border: 'none', cursor: 'pointer',
                            borderLeft: index === 0 ? 'none' : '1px solid #ccc',
                            fontSize: '0.8rem', fontWeight: 500,
                            backgroundColor: mode === option.value ? 'var(--rox-lilac-ink)' : '#fff',
                            color: mode === option.value ? '#fff' : '#555',
                            transition: 'background-color 0.15s'
                        }}
                    >
                        <Icon style={{ fontSize: '16px' }} /> {option.label}
                    </button>
                );
            })}
    </div>
);

const WarningBanner = ({ children }) => (
    <div style={{
        display: 'flex',
        gap: '12px',
        alignItems: 'flex-start',
        padding: '12px 16px',
        backgroundColor: '#fff8e1',
        border: '1px solid #ffe082',
        borderRadius: '8px',
        marginBottom: '16px',
        color: '#795548',
        fontSize: '0.875rem'
    }}>
        <WarningIcon style={{ color: '#f9a825', fontSize: '20px', flexShrink: 0 }} />
        <div>{children}</div>
    </div>
);

const DocusignTemplatesTab = ({ clientUuid, numericClientId }) => {
    const { clientId } = useParams(); // route param, holds the client uuid
    const [resolvedClientUuid, setResolvedClientUuid] = useState(clientUuid || null);
    const [resolvedNumericId, setResolvedNumericId] = useState(numericClientId || null);
    const [fetchingClient, setFetchingClient] = useState(false);

    const [templateType, setTemplateType] = useState(DOCUSIGN_TEMPLATE_TYPES[0].value);
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    // Lookups from the customer-scoped Docusign endpoints; unavailable when the logged in
    // user cannot read another client's Docusign account, in which case the ids are typed in.
    const [connection, setConnection] = useState(null);
    const [accounts, setAccounts] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [lookupsAvailable, setLookupsAvailable] = useState(true);

    const [formAccountId, setFormAccountId] = useState('');
    const [formTemplateId, setFormTemplateId] = useState('');
    const [formRoles, setFormRoles] = useState([]);
    const [errors, setErrors] = useState({});
    const [pendingWarnings, setPendingWarnings] = useState([]);
    const [pendingConfiguration, setPendingConfiguration] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const [inputMode, setInputMode] = useState('form'); // 'form' | 'json'
    const [jsonText, setJsonText] = useState('');
    const [jsonError, setJsonError] = useState('');
    const [jsonCopied, setJsonCopied] = useState(false);

    useEffect(() => {
        if (clientUuid) setResolvedClientUuid(clientUuid);
        if (numericClientId) setResolvedNumericId(numericClientId);
    }, [clientUuid, numericClientId]);

    useEffect(() => {
        if ((clientUuid && numericClientId) || !clientId || fetchingClient) return;
        if (resolvedClientUuid && resolvedNumericId) return;

        setFetchingClient(true);
        request(`clients/${clientId}`, 'GET', null, false, {}, null, false)
            .then((response) => {
                setResolvedClientUuid(response?.uuid);
                setResolvedNumericId(response?.clientId);
            })
            .catch(() => setLoading(false))
            .finally(() => setFetchingClient(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientId, clientUuid, numericClientId]);

    const fetchTemplatesForAccount = useCallback((accountId) => {
        if (!resolvedNumericId || !accountId) {
            setTemplates([]);
            return;
        }
        getDocusignTemplatesByClientId(resolvedNumericId, accountId)
            .then((result) => setTemplates(result?.response || []))
            .catch(() => setTemplates([]));
    }, [resolvedNumericId]);

    useEffect(() => {
        if (!resolvedNumericId) return;

        getDocusignConfigByClientId(resolvedNumericId)
            .then((result) => {
                const docusignConfig = result?.response || {};
                setConnection(docusignConfig);
                if (docusignConfig.url) return; // OAuth pending: no account to list templates for

                getDocusignAccountsByClientId(resolvedNumericId)
                    .then((accountsResult) => setAccounts(accountsResult?.response || []))
                    .catch(() => setAccounts([]));

                if (docusignConfig.accountId) fetchTemplatesForAccount(docusignConfig.accountId);
            })
            .catch(() => {
                setConnection(null);
                setLookupsAvailable(false);
            });
    }, [resolvedNumericId, fetchTemplatesForAccount]);

    const fetchConfig = useCallback(() => {
        if (!resolvedClientUuid) return;
        setLoading(true);
        getDocusignTemplateConfig(resolvedClientUuid, templateType)
            .then((response) => setConfig(response || null))
            .catch(() => setConfig(null))
            .finally(() => setLoading(false));
    }, [resolvedClientUuid, templateType]);

    useEffect(() => {
        fetchConfig();
    }, [fetchConfig]);

    /** Stored configuration turned into editable state; shared by the form and the JSON view. */
    const initialConfiguration = useCallback(() => {
        const roles = (config?.roles || []).map((role, roleIndex) => ({
            tempId: `role-${roleIndex}`,
            name: role.name || '',
            roleOnDocusignTemplate: role.roleOnDocusignTemplate || '',
            fields: (role.fields || []).map((field, fieldIndex) => ({
                tempId: `field-${roleIndex}-${fieldIndex}`,
                templateName: field.templateName || '',
                internalName: field.internalName || ''
            }))
        }));
        return {
            accountId: config?.accountId || connection?.accountId || '',
            templateId: config?.templateId || '',
            // nothing configured yet: start from the two roles the backend knows how to map
            roles: roles.length ? roles : DOCUSIGN_ROLE_NAMES.map((option) => ({
                ...emptyRole(),
                name: option.value,
                fields: []
            }))
        };
    }, [config, connection]);

    const resetForm = useCallback(() => {
        const initial = initialConfiguration();
        setFormAccountId(initial.accountId);
        setFormTemplateId(initial.templateId);
        setFormRoles(initial.roles);
        setErrors({});
    }, [initialConfiguration]);

    const handleEdit = () => {
        const initial = initialConfiguration();
        resetForm();
        fetchTemplatesForAccount(initial.accountId);
        // keeps whichever view the user was reading in
        if (inputMode === 'json') setJsonText(JSON.stringify(buildConfigurationPayload(initial), null, 2));
        setJsonError('');
        setIsEditing(true);
    };

    const handleCancel = () => {
        setIsEditing(false);
        setInputMode('form');
        setJsonError('');
        resetForm();
    };

    const handleAccountChange = (accountId) => {
        setFormAccountId(accountId);
        setFormTemplateId('');
        fetchTemplatesForAccount(accountId);
    };

    /** The JSON shown while editing is exactly the body the PUT endpoint expects. */
    const currentPayload = () => buildConfigurationPayload({
        roles: formRoles,
        accountId: formAccountId,
        templateId: formTemplateId
    });

    const readOnlyJson = JSON.stringify(config || {}, null, 2);

    const copyJson = (text) => {
        if (!navigator.clipboard?.writeText) return;
        navigator.clipboard.writeText(text)
            .then(() => {
                setJsonCopied(true);
                setTimeout(() => setJsonCopied(false), 2000);
            })
            .catch(() => setJsonCopied(false));
    };

    const handleModeChange = (mode) => {
        if (mode === inputMode) return;
        if (!isEditing) {
            setInputMode(mode);
            return;
        }
        if (mode === 'json') {
            setJsonText(JSON.stringify(currentPayload(), null, 2));
            setJsonError('');
            setInputMode('json');
            return;
        }
        // back to the form: the JSON is applied so both views stay in sync
        if (applyJson()) setInputMode('form');
    };

    /**
     * Parses the JSON textarea into the form state. Returns the parsed configuration, or
     * null when the JSON is invalid (the message is shown under the editor).
     */
    const parseJson = () => {
        let parsed;
        try {
            parsed = JSON.parse(jsonText);
        } catch (e) {
            setJsonError(`Invalid JSON: ${e.message}`);
            return null;
        }

        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
            setJsonError('Invalid JSON: expected an object with accountId, templateId and roles.');
            return null;
        }
        if (parsed.roles !== undefined && !Array.isArray(parsed.roles)) {
            setJsonError('Invalid JSON: "roles" must be an array.');
            return null;
        }
        if ((parsed.roles || []).some((role) => !role || typeof role !== 'object' || Array.isArray(role))) {
            setJsonError('Invalid JSON: every entry of "roles" must be an object.');
            return null;
        }
        if ((parsed.roles || []).some((role) => role.fields !== undefined && !Array.isArray(role.fields))) {
            setJsonError('Invalid JSON: "fields" must be an array on every role.');
            return null;
        }

        setJsonError('');
        return {
            accountId: typeof parsed.accountId === 'string' ? parsed.accountId : '',
            templateId: typeof parsed.templateId === 'string' ? parsed.templateId : '',
            roles: (parsed.roles || []).map((role, roleIndex) => ({
                tempId: `json-role-${roleIndex}`,
                name: role.name || '',
                roleOnDocusignTemplate: role.roleOnDocusignTemplate || '',
                fields: (role.fields || []).map((field, fieldIndex) => ({
                    tempId: `json-field-${roleIndex}-${fieldIndex}`,
                    templateName: field?.templateName || '',
                    internalName: field?.internalName || ''
                }))
            }))
        };
    };

    const applyJson = () => {
        const parsed = parseJson();
        if (!parsed) return null;

        setFormAccountId(parsed.accountId);
        setFormTemplateId(parsed.templateId);
        setFormRoles(parsed.roles);
        setErrors({});
        if (parsed.accountId && parsed.accountId !== formAccountId) fetchTemplatesForAccount(parsed.accountId);
        return parsed;
    };

    const persist = (configuration) => {
        setSubmitting(true);
        configureDocusignTemplate(resolvedClientUuid, templateType, buildConfigurationPayload(configuration))
            .then(() => {
                Toast({ type: 'success', message: 'DocuSign template configuration updated successfully.' });
                setIsEditing(false);
                setInputMode('form');
                fetchConfig();
            })
            .catch(() => {})
            .finally(() => setSubmitting(false));
    };

    const handleSave = () => {
        // in JSON mode the text is the source of truth, so it is applied before validating
        const candidate = inputMode === 'json' ? applyJson() : currentPayload();
        if (!candidate) return;

        const validation = validateTemplateConfiguration(candidate);
        setErrors(validation.errors);

        if (validation.hasErrors) {
            Toast({ type: 'error', message: 'Fix the highlighted fields before saving.' });
            if (inputMode === 'json') setInputMode('form');
            return;
        }

        if (validation.warnings.length) {
            setPendingWarnings(validation.warnings);
            setPendingConfiguration(candidate);
            setConfirmOpen(true);
            return;
        }

        persist(candidate);
    };

    const handleConfirmSave = () => {
        setConfirmOpen(false);
        persist(pendingConfiguration || currentPayload());
    };

    const needsOauth = !!connection?.url || (lookupsAvailable && connection && !connection.accountId);
    const selectedTemplate = templates.find((template) => template.templateId === formTemplateId);

    if (loading && !config) {
        return (
            <Card className='p-4 cardHeight'>
                <div className='d-flex align-items-center justify-content-center h-100'>
                    <CircularProgress size={48} color='primary' />
                </div>
            </Card>
        );
    }

    if (!resolvedClientUuid) {
        return (
            <Card className='p-4'>
                <div style={{ color: '#999', textAlign: 'center', padding: '32px 0' }}>
                    The client could not be resolved, so the DocuSign template configuration cannot be loaded.
                </div>
            </Card>
        );
    }

    return (
        <>
            <Card className='p-4'>
                <FormControl variant="outlined" fullWidth size="small">
                    <InputLabel id="docusign-template-type-label">Template</InputLabel>
                    <Select
                        labelId="docusign-template-type-label"
                        label="Template"
                        value={templateType}
                        disabled={isEditing || loading}
                        onChange={(e) => setTemplateType(e.target.value)}
                    >
                        {DOCUSIGN_TEMPLATE_TYPES.map((type) => (
                            <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Card>

            <Card className='p-4 mt-3'>
                <h5 style={{ marginBottom: '24px' }}>DocuSign Connection</h5>
                {needsOauth && (
                    <WarningBanner>
                        This client has not connected a DocuSign account yet. The configuration can be saved,
                        but the template stays unlinked and sending an envelope fails with
                        <strong> "Docusign Template not set"</strong> until the OAuth connection is completed
                        from the client's Docusign settings.
                    </WarningBanner>
                )}
                {!lookupsAvailable && (
                    <WarningBanner>
                        The DocuSign account and template lists could not be read for this client, so the
                        account and template ids have to be typed in. Make sure the template id exists in that
                        account: an unknown id makes the save fail on the DocuSign side.
                    </WarningBanner>
                )}
                <div style={{ backgroundColor: 'var(--bg-canvas)', borderRadius: '8px', padding: '8px 16px' }}>
                    <ConfigItem label="Account Connected" value={lookupsAvailable && connection ? !needsOauth : 'Unknown'} />
                    <ConfigItem label="Account Name" value={connection?.accountName} />
                    <ConfigItem label="Connected Account ID" value={connection?.accountId} />
                    <ConfigItem label="Default Template" value={connection?.templateName} />
                </div>
            </Card>

            <Card className='p-4 mt-3 mb-3'>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '24px'
                }}>
                    <h5 style={{ margin: 0 }}>Template Field Configuration</h5>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <ModeToggle
                            mode={inputMode}
                            onChange={handleModeChange}
                            formLabel={isEditing ? 'Form' : 'Detail'}
                        />
                        {inputMode === 'json' && (
                            <Button
                                size="small"
                                startIcon={<FileCopyIcon />}
                                onClick={() => copyJson(isEditing ? jsonText : readOnlyJson)}
                                style={{ color: jsonCopied ? 'var(--status-approved)' : undefined }}
                            >
                                {jsonCopied ? 'Copied!' : 'Copy'}
                            </Button>
                        )}
                        {isEditing ? (
                            <>
                                <Button onClick={handleCancel} disabled={submitting} startIcon={<CloseIcon />}>
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleSave}
                                    disabled={submitting}
                                    startIcon={submitting ? <CircularProgress size={16} /> : <SaveIcon />}
                                >
                                    Save
                                </Button>
                            </>
                        ) : (
                            <Button variant="contained" color="primary" startIcon={<EditIcon />} onClick={handleEdit}>
                                Edit Configuration
                            </Button>
                        )}
                    </div>
                </div>

                {isEditing && inputMode === 'json' ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <WarningBanner>
                            This is the body sent to the endpoint. Editing it replaces every role and field of
                            the template, and <code>internalName</code> only accepts the values of the catalog.
                            Switching back to <strong>Form</strong> applies the JSON.
                        </WarningBanner>
                        <JsonEditor value={jsonText} onChange={setJsonText} error={jsonError} />
                    </div>
                ) : !isEditing && inputMode === 'json' ? (
                    <JsonEditor value={readOnlyJson} onChange={() => {}} readOnly />
                ) : isEditing ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        <WarningBanner>
                            Saving replaces every role and field of this template: whatever is not sent is deleted.
                            The DocuSign role and the tab label have to match the DocuSign template exactly.
                        </WarningBanner>

                        <div style={{ display: 'flex', gap: '12px' }}>
                            {lookupsAvailable && accounts.length > 0 ? (
                                <FormControl variant="outlined" size="small" style={{ flex: 1 }}>
                                    <InputLabel id="docusign-account-label">DocuSign account</InputLabel>
                                    <Select
                                        labelId="docusign-account-label"
                                        label="DocuSign account"
                                        value={formAccountId}
                                        onChange={(e) => handleAccountChange(e.target.value)}
                                    >
                                        {accounts.map((account) => (
                                            <MenuItem key={account.account_id} value={account.account_id}>
                                                {account.account_name || account.base_uri}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            ) : (
                                <TextField
                                    label="DocuSign account id"
                                    variant="outlined"
                                    size="small"
                                    style={{ flex: 1 }}
                                    value={formAccountId}
                                    onChange={(e) => setFormAccountId(e.target.value)}
                                    helperText="Leaving it empty keeps the stored value"
                                />
                            )}

                            {lookupsAvailable && templates.length > 0 ? (
                                <FormControl variant="outlined" size="small" style={{ flex: 1 }}>
                                    <InputLabel id="docusign-template-label">DocuSign template</InputLabel>
                                    <Select
                                        labelId="docusign-template-label"
                                        label="DocuSign template"
                                        value={formTemplateId}
                                        onChange={(e) => setFormTemplateId(e.target.value)}
                                    >
                                        {templates.map((template) => (
                                            <MenuItem key={template.templateId} value={template.templateId}>
                                                {template.name}
                                            </MenuItem>
                                        ))}
                                        {formTemplateId && !selectedTemplate && (
                                            <MenuItem value={formTemplateId}>{formTemplateId} (not in this account)</MenuItem>
                                        )}
                                    </Select>
                                </FormControl>
                            ) : (
                                <TextField
                                    label="DocuSign template id"
                                    variant="outlined"
                                    size="small"
                                    style={{ flex: 1 }}
                                    value={formTemplateId}
                                    onChange={(e) => setFormTemplateId(e.target.value)}
                                    helperText="Leaving it empty keeps the stored value"
                                />
                            )}
                        </div>

                        <DocusignRolesEditor
                            roles={formRoles}
                            onChange={setFormRoles}
                            errors={errors}
                            disabled={submitting}
                        />
                    </div>
                ) : (
                    <>
                        <div style={{ backgroundColor: 'var(--bg-canvas)', borderRadius: '8px', padding: '8px 16px', marginBottom: '24px' }}>
                            <ConfigItem label="Template Type" value={getTemplateTypeLabel(config?.templateType || templateType)} />
                            <ConfigItem label="Account ID" value={config?.accountId} />
                            <ConfigItem label="Template ID" value={config?.templateId} />
                            <ConfigItem label="Template Name" value={config?.templateName} />
                        </div>
                        <DocusignRolesView roles={config?.roles || []} />
                    </>
                )}
            </Card>

            <Dialog open={confirmOpen} fullWidth maxWidth="sm">
                <DialogTitle>Save an incomplete configuration?</DialogTitle>
                <DialogContent>
                    <DialogContentText component="div">
                        The configuration is valid, but sending the envelope may fail:
                        <ul style={{ marginTop: '12px', paddingLeft: '20px' }}>
                            {pendingWarnings.map((warning, index) => (
                                <li key={index} style={{ marginBottom: '8px' }}>{warning}</li>
                            ))}
                        </ul>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="primary" onClick={handleConfirmSave}>Save anyway</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default DocusignTemplatesTab;
